import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";

import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { normalizeCreator } from "../utils/homeSeedPreview";
import { fetchFeedDrops, isOpenFeedDrop } from "../services/dropsFeedService";
import { fetchCreators } from "../services/userQueryService";
import { loadGalleryFeaturedCreators } from "../services/featuredService";
import { buildGalleryFeaturedSlides, useRevolvingFeatured } from "../utils/revolvingFeatured";
import FeaturedCard from "../components/featured/FeaturedCard";
import ProfileAvatarLink from "../components/profile/ProfileAvatarLink";
import ProfileNameLink from "../components/profile/ProfileNameLink";
import {
  getNicheMeta,
  NICHE_BROWSE_LIST,
  normalizeNicheKey,
} from "../components/niche";
import SessionDismissible, { NUDGE_IDS } from "../components/nudges/SessionDismissible";
import usePaidPlacement from "../hooks/usePaidPlacement";
import { SkeletonDropGrid } from "../components/discovery/SkeletonCard";
import DropCard from "../components/discovery/DropCard";
import CreatorCard from "../components/discovery/CreatorCard";
import "./Discover.css";

const WEEKLY_SPOTLIGHT = {
  username: "sophiakate",
  quote:
    "I wanted a place where my real fans could see things before anyone else. 100ViewsOnly is exactly that.",
  featuredMomentCaption: "Golden hour in Banff 🏔️",
};

const NICHE_FILTERS = [{ key: "all", label: "All" }, ...NICHE_BROWSE_LIST];

// ── Research constants ───────────────────────────────────────────
// A creator counts as "new" (Rising rail eligibility) if EITHER of these
// holds. We check both because we don't know for certain which field is
// populated for every creator doc — verify with your actual schema and
// drop whichever check doesn't apply.
const NEW_CREATOR_WINDOW_DAYS = 14;
const NEW_CREATOR_MAX_DROPS = 2;
const FRESH_MOMENTS_LIMIT = 10;
const RISING_LIMIT = 6;
const NEAR_YOU_LIMIT = 6;

// Rotating search placeholder — honest examples that reflect what
// search can actually do TODAY (keyword match against titles,
// creators, niches). Once the AI-assisted search backend from the
// earlier spec ships, swap these for natural-language examples —
// not before, since promising more than the search bar can deliver
// would teach people to distrust it the first time they try it.
// Module-level (not inside the component) so it's a stable reference
// across renders — otherwise the rotation useEffect would need it in
// its dependency array and re-fire the interval every render.
const SEARCH_PLACEHOLDERS = [
  "Search creators, niches, topics...",
  "Try \"wellness\"...",
  "Try \"Toronto\"...",
  "Try a creator's name...",
];

function IconSearch() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function IconBolt() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  );
}

function IconClock() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" />
    </svg>
  );
}

function IconSpark() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2l1.6 5.8L19 9l-5.4 1.2L12 16l-1.6-5.8L5 9l5.4-1.2z" />
    </svg>
  );
}

function IconPin() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 21s-7-6.1-7-11a7 7 0 0 1 14 0c0 4.9-7 11-7 11z" /><circle cx="12" cy="10" r="2.5" />
    </svg>
  );
}

function viewsLeft(drop) {
  return Math.max((drop.maxViews || 100) - (drop.viewCount || 0), 0);
}

function toMillis(value) {
  if (!value) return 0;
  if (typeof value.toDate === "function") return value.toDate().getTime();
  if (typeof value === "number") return value;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

// Deterministic-per-session shuffle. Same seed => same order for the
// whole session (so rails don't visibly jump around while browsing),
// but a fresh seed next visit. This is the "fair rotation, not an
// algorithm" mechanic from the Discovery research — it is NOT based on
// any user behavior, just an honest random draw from the eligible pool.
function seededShuffle(arr, seed) {
  const a = [...arr];
  let s = seed;
  for (let i = a.length - 1; i > 0; i--) {
    s = (s * 9301 + 49297) % 233280;
    const j = Math.floor((s / 233280) * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function getDropAccess(drop, purchasedDropIds, subscribedCreatorIds) {
  if (drop.status === "closed") return { type: "closed" };
  if (drop.accessType === "free" || drop.price === 0) return { type: "free" };
  if (drop.accessType === "paid") {
    return purchasedDropIds.has(drop.id)
      ? { type: "paid-unlocked" }
      : { type: "paid-locked" };
  }
  if (drop.accessType === "subscription") {
    return subscribedCreatorIds.has(drop.creatorId)
      ? { type: "sub-unlocked" }
      : { type: "sub-locked" };
  }
  return { type: "free" };
}

function matchesSearch(text, q) {
  if (!q) return true;
  return text.toLowerCase().includes(q.toLowerCase());
}

// A creator qualifies for the "Rising · New to 100VO" reserved rail if
// they're recently joined OR have posted very few drops so far. This is
// a guaranteed-exposure rule, not a ranked list — it can't be earned by
// performance and can't be bought.
function isNewCreator(creator, totalDrops) {
  const joinedMillis = toMillis(creator.createdAt || creator.joinedAt);
  const withinWindow =
    joinedMillis > 0 &&
    Date.now() - joinedMillis < NEW_CREATOR_WINDOW_DAYS * 24 * 60 * 60 * 1000;
  const lowDropCount = (totalDrops ?? 0) <= NEW_CREATOR_MAX_DROPS;
  return withinWindow || lowDropCount;
}

export default function Discover() {
  const { currentUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const { placement: paidPlacement } = usePaidPlacement();

  const [creators, setCreators] = useState([]);
  const [moments, setMoments] = useState([]);
  const [drops, setDrops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [followingIds, setFollowingIds] = useState([]);
  const [followedByNetwork, setFollowedByNetwork] = useState([]);
  const [spotlightCreator, setSpotlightCreator] = useState(null);
  const [purchasedDropIds, setPurchasedDropIds] = useState(new Set());
  const [subscribedCreatorIds, setSubscribedCreatorIds] = useState(new Set());
  const [galleryFeaturedCreators, setGalleryFeaturedCreators] = useState([]);
  const [currentUserCity, setCurrentUserCity] = useState("");

  // Session seed for fair rotation — stable for this browsing session,
  // fresh on next visit. Never derived from user behavior.
  const [sessionSeed] = useState(() => Math.floor(Math.random() * 1_000_000));

  const initialNiche = searchParams.get("niche") || "all";
  const [activeNiche, setActiveNiche] = useState(
    initialNiche === "all" ? "all" : normalizeNicheKey(initialNiche)
  );
  const [searchQuery, setSearchQuery] = useState("");

  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [searchFocused, setSearchFocused] = useState(false);

  useEffect(() => {
    if (searchFocused || searchQuery) return; // pause while typing or focused
    const interval = setInterval(() => {
      setPlaceholderIndex((i) => (i + 1) % SEARCH_PLACEHOLDERS.length);
    }, 3200);
    return () => clearInterval(interval);
  }, [searchFocused, searchQuery]);

  useEffect(() => {
    const nicheParam = searchParams.get("niche");
    if (nicheParam) {
      setActiveNiche(nicheParam === "all" ? "all" : normalizeNicheKey(nicheParam));
    }
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const [creatorList, momentsSnap, dropsList] = await Promise.all([
          fetchCreators(100),
          getDocs(collection(db, "moments")),
          fetchFeedDrops(50),
        ]);
        if (!cancelled) {
          setCreators(creatorList);
          setMoments(momentsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
          setDrops(dropsList);
        }

        const spotSnap = await getDocs(
          query(collection(db, "users"), where("username", "==", WEEKLY_SPOTLIGHT.username))
        );
        if (!spotSnap.empty && !cancelled) {
          setSpotlightCreator({ id: spotSnap.docs[0].id, ...spotSnap.docs[0].data() });
        }

        // NOTE: intentionally no "boosted" pay-to-rank fetch here anymore.
        // See the audit notes for why it was removed rather than kept as
        // a ranking input.
      } catch (err) {
        console.error("Discover load error:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    loadGalleryFeaturedCreators()
      .then((creators) => {
        if (!cancelled) setGalleryFeaturedCreators(creators);
      })
      .catch((err) => {
        console.error("Gallery featured hero load error:", err);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!currentUser) return;
    getDocs(query(collection(db, "follows"), where("followerId", "==", currentUser.uid)))
      .then((snap) => setFollowingIds(snap.docs.map((d) => d.data().creatorId)))
      .catch(console.error);

    getDoc(doc(db, "users", currentUser.uid))
      .then((snap) => {
        if (snap.exists()) {
          const data = snap.data();
          setPurchasedDropIds(new Set(data.purchasedDrops || []));
          setSubscribedCreatorIds(new Set(data.subscriptions || []));
          // Reused for the "Near You" rail — same field CreatorCard
          // already reads (creator.city), just read off the viewer's
          // own profile too. If your schema stores location elsewhere
          // (e.g. data.location.city), adjust this line.
          setCurrentUserCity(data.city || "");
        }
      })
      .catch(console.error);
  }, [currentUser]);

  useEffect(() => {
    if (!followingIds.length || !creators.length) return;
    const load = async () => {
      try {
        const networkFollows = await Promise.all(
          followingIds.slice(0, 10).map((id) =>
            getDocs(query(collection(db, "follows"), where("followerId", "==", id)))
          )
        );
        const countMap = {};
        networkFollows.forEach((snap) => {
          snap.docs.forEach((d) => {
            const cid = d.data().creatorId;
            if (!followingIds.includes(cid) && cid !== currentUser?.uid) {
              countMap[cid] = (countMap[cid] || 0) + 1;
            }
          });
        });
        const sorted = Object.entries(countMap)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 4)
          .map(([id, count]) => {
            const c = creators.find((cr) => cr.id === id || cr.uid === id);
            return c ? { ...c, networkCount: count } : null;
          })
          .filter(Boolean);
        setFollowedByNetwork(sorted);
      } catch (err) {
        console.error(err);
      }
    };
    load();
  }, [followingIds, creators, currentUser]);

  const creatorById = useMemo(() => {
    const map = new Map();
    creators.forEach((c) => {
      map.set(c.id, normalizeCreator(c.id, c));
      if (c.uid) map.set(c.uid, normalizeCreator(c.id, c));
    });
    return map;
  }, [creators]);

  const openDropCount = useMemo(
    () => drops.filter(isOpenFeedDrop).length,
    [drops]
  );

  const dropsByCreator = useMemo(() => {
    const map = new Map();
    drops.forEach((d) => {
      const list = map.get(d.creatorId) || [];
      list.push(d);
      map.set(d.creatorId, list);
    });
    return map;
  }, [drops]);

  const trimmedSearch = searchQuery.trim();

  const featuredSlides = useMemo(
    () => buildGalleryFeaturedSlides(galleryFeaturedCreators, drops),
    [galleryFeaturedCreators, drops]
  );

  const {
    slide: featuredSlide,
    visible: heroVisible,
  } = useRevolvingFeatured(featuredSlides, 8000);

  const featuredDropId = featuredSlide?.drop?.id;

  // ── FRESH MOMENTS — chronological, newest first. This is the
  // freshness pillar from the research: every open drop gets a shot at
  // this rail purely by being recent, independent of category or
  // creator size. ──────────────────────────────────────────────────
  const freshMoments = useMemo(() => {
    return drops
      .filter((d) => isOpenFeedDrop(d) && d.id !== featuredDropId)
      .filter((d) => {
        if (activeNiche !== "all" && normalizeNicheKey(d.niche) !== activeNiche) return false;
        if (trimmedSearch) {
          const creator = creatorById.get(d.creatorId);
          const haystack = [d.title, d.description, creator?.displayName, creator?.handle, getNicheMeta(d.niche).label]
            .filter(Boolean)
            .join(" ");
          if (!matchesSearch(haystack, trimmedSearch)) return false;
        }
        return true;
      })
      .sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt));
  }, [drops, featuredDropId, activeNiche, trimmedSearch, creatorById]);

  // Full pool size for the rail's "See all" link — freshMoments above is
  // used for the rail itself (unsliced now; slice happens at render).
  const freshMomentsTotal = freshMoments.length;
  const freshMomentsRail = useMemo(
    () => freshMoments.slice(0, FRESH_MOMENTS_LIMIT),
    [freshMoments]
  );

  // ── CLOSING NOW / Almost Locked — ranked purely by views remaining.
  // No boost, no engagement weighting. This is intentionally the ONE
  // ranked section on the page, because it ranks a fact, not a score. ─
  const closingDrops = useMemo(() => {
    const freshIds = new Set(freshMomentsRail.map((d) => d.id)); // anti-repeat against the RAIL only, not the full pool
    return drops
      .filter((d) => isOpenFeedDrop(d))
      .filter((d) => {
        if (d.id === featuredDropId) return false;
        if (freshIds.has(d.id)) return false; // anti-repeat: don't duplicate the Fresh rail
        if (activeNiche !== "all" && normalizeNicheKey(d.niche) !== activeNiche) return false;
        if (trimmedSearch) {
          const creator = creatorById.get(d.creatorId);
          const haystack = [d.title, d.description, creator?.displayName, creator?.handle, getNicheMeta(d.niche).label]
            .filter(Boolean)
            .join(" ");
          if (!matchesSearch(haystack, trimmedSearch)) return false;
        }
        return true;
      })
      .sort((a, b) => viewsLeft(a) - viewsLeft(b));
  }, [drops, featuredDropId, freshMomentsRail, activeNiche, trimmedSearch, creatorById]);

  // ── RISING · NEW TO 100VO — reserved rail. Eligibility is
  // structural (recently joined / low drop count), and the order
  // within the pool is a fair session-seeded shuffle, never a
  // performance ranking. ──────────────────────────────────────────
  const risingCreators = useMemo(() => {
    const pool = creators
      .map((c) => {
        const creatorDrops = dropsByCreator.get(c.id) || [];
        const openCount = creatorDrops.filter(isOpenFeedDrop).length;
        return { ...normalizeCreator(c.id, c), openCount, totalDrops: creatorDrops.length };
      })
      .filter((c) => isNewCreator(c, c.totalDrops))
      .filter((c) => {
        if (activeNiche !== "all" && normalizeNicheKey(c.niche) !== activeNiche) return false;
        if (trimmedSearch) {
          const haystack = [c.displayName, c.handle, c.bio, getNicheMeta(c.niche).label].filter(Boolean).join(" ");
          if (!matchesSearch(haystack, trimmedSearch)) return false;
        }
        return true;
      });
    return seededShuffle(pool, sessionSeed);
  }, [creators, dropsByCreator, activeNiche, trimmedSearch, sessionSeed]);

  const risingTotal = risingCreators.length;
  const risingRail = useMemo(
    () => risingCreators.slice(0, RISING_LIMIT),
    [risingCreators]
  );
  const risingIds = useMemo(() => new Set(risingCreators.map((c) => c.id)), [risingCreators]);

  // ── CREATORS TO FOLLOW — general discovery grid. Network overlap
  // (an explicit signal from who YOU follow) still ranks first; beyond
  // that, ties are broken by the session-seeded shuffle instead of raw
  // follower count, so this doesn't quietly become a popularity list. ─
  const creatorsToFollow = useMemo(() => {
    const networkIds = new Set(followedByNetwork.map((c) => c.id));
    const pool = creators
      .filter((c) => !followingIds.includes(c.id))
      .filter((c) => !risingIds.has(c.id)) // anti-repeat: Rising has its own rail
      .map((c) => {
        const creatorDrops = dropsByCreator.get(c.id) || [];
        const openCount = creatorDrops.filter(isOpenFeedDrop).length;
        return { ...normalizeCreator(c.id, c), openCount, totalDrops: creatorDrops.length };
      })
      .filter((c) => {
        if (activeNiche !== "all") {
          const nicheKey = normalizeNicheKey(c.niche);
          if (nicheKey !== activeNiche) return false;
        }
        if (searchQuery.trim()) {
          const haystack = [c.displayName, c.handle, c.bio, getNicheMeta(c.niche).label]
            .filter(Boolean)
            .join(" ");
          if (!matchesSearch(haystack, searchQuery)) return false;
        }
        return c.openCount > 0 || c.totalDrops > 0;
      });

    const shuffled = seededShuffle(pool, sessionSeed + 1);
    return shuffled
      .sort((a, b) => {
        const aNet = networkIds.has(a.id) ? 1 : 0;
        const bNet = networkIds.has(b.id) ? 1 : 0;
        if (bNet !== aNet) return bNet - aNet;
        return b.openCount - a.openCount; // no followerCount tiebreak
      })
      .slice(0, 3);
  }, [creators, followingIds, risingIds, followedByNetwork, dropsByCreator, activeNiche, searchQuery, sessionSeed]);

  // ── NEAR YOU — opt-in-by-existing-profile-field location lane.
  // v1: exact city-string match against the viewer's own profile city.
  // No distance math, no third-party geocoding — cheap, honest, and
  // gracefully hides itself if either side has no city set. ─────────
  const nearYouCreators = useMemo(() => {
    if (!currentUserCity) return [];
    const pool = creators
      .filter((c) => c.city && c.city.toLowerCase() === currentUserCity.toLowerCase())
      .filter((c) => !followingIds.includes(c.id))
      .map((c) => {
        const creatorDrops = dropsByCreator.get(c.id) || [];
        const openCount = creatorDrops.filter(isOpenFeedDrop).length;
        return { ...normalizeCreator(c.id, c), openCount, totalDrops: creatorDrops.length };
      })
      .filter((c) => c.openCount > 0 || c.totalDrops > 0);
    return seededShuffle(pool, sessionSeed + 2).slice(0, NEAR_YOU_LIMIT);
  }, [creators, currentUserCity, followingIds, dropsByCreator, sessionSeed]);

  const spotlightMoment = useMemo(() => {
    if (!spotlightCreator?.id) return null;
    return moments.find((m) => m.creatorId === spotlightCreator.id) || null;
  }, [moments, spotlightCreator]);

  const reengagementCreator = useMemo(() => {
    const candidate =
      creatorsToFollow[0] ||
      (spotlightCreator && !followingIds.includes(spotlightCreator.id) ? spotlightCreator : null) ||
      creators.find((c) => !followingIds.includes(c.id));
    if (!candidate) return null;
    const normalized = normalizeCreator(candidate.id, candidate);
    const creatorDrops = dropsByCreator.get(candidate.id) || [];
    const openDrop = creatorDrops.find(isOpenFeedDrop) || creatorDrops[0];
    return { creator: normalized, drop: openDrop, moment: spotlightMoment };
  }, [creatorsToFollow, spotlightCreator, creators, followingIds, dropsByCreator, spotlightMoment]);

  const creatorsSeenCount = useMemo(() => {
    const ids = new Set(closingDrops.map((d) => d.creatorId));
    freshMoments.forEach((d) => ids.add(d.creatorId));
    if (featuredSlide?.creator?.id) ids.add(featuredSlide.creator.id);
    return ids.size;
  }, [closingDrops, freshMoments, featuredSlide]);

  const streakCount = Math.min(creatorsToFollow.length + (featuredSlide ? 1 : 0), 3);

  // Resolve today's paid placement (if any) into the actual drop or
  // creator object it points to. Additive, not exclusive: this
  // creator/drop can ALSO still appear organically in Closing Now /
  // Fresh Moments / Rising if it genuinely qualifies there — paying
  // adds one guaranteed slot, it doesn't remove them from anywhere else.
  const paidItem = useMemo(() => {
    if (!paidPlacement) return null;
    if (paidPlacement.itemType === "drop") {
      const drop = drops.find((d) => d.id === paidPlacement.itemId);
      if (!drop) return null;
      const creator =
        creatorById.get(drop.creatorId) ||
        normalizeCreator(drop.creatorId, { displayName: "Creator", avatarUrl: "" });
      return { type: "drop", drop, creator };
    }
    if (paidPlacement.itemType === "creator") {
      const creator = creatorById.get(paidPlacement.itemId);
      if (!creator) return null;
      const creatorDrops = dropsByCreator.get(creator.id) || [];
      const openCount = creatorDrops.filter(isOpenFeedDrop).length;
      return { type: "creator", creator: { ...creator, openCount } };
    }
    return null;
  }, [paidPlacement, drops, creatorById, dropsByCreator]);

  const handleNicheChange = (key) => {
    setActiveNiche(key);
    if (key === "all") {
      searchParams.delete("niche");
    } else {
      searchParams.set("niche", key);
    }
    setSearchParams(searchParams, { replace: true });
  };

  if (loading) {
    return (
      <div className="gal-page">
        <div className="gal-grid-page">
          <div className="gal-closing-grid">
            <SkeletonDropGrid count={6} />
          </div>
        </div>
      </div>
    );
  }

  const showHero = Boolean(featuredSlide);

  return (
    <div className="gal-page">
      {/* 1. FEATURED HERO — unchanged; this is the deliberately editorial
          section (Product Hunt / Snapchat Discover model), same for
          every visitor, refreshes on its own schedule. */}
      {showHero && featuredSlide && (
        <FeaturedCard
          variant="gallery"
          creator={featuredSlide.creator}
          drop={featuredSlide.drop}
          access={getDropAccess(
            featuredSlide.drop,
            purchasedDropIds,
            subscribedCreatorIds
          )}
          followingIds={followingIds}
          visible={heroVisible}
        />
      )}

      {/* 2. SEARCH + FILTER BAR */}
      <div className="gal-search-row">
        <div className={`gal-search-pill${searchFocused ? " gal-search-pill--focused" : ""}`}>
          <IconSearch />
          <input
            type="search"
            placeholder={SEARCH_PLACEHOLDERS[placeholderIndex]}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            aria-label="Search gallery"
          />
        </div>
        <div className="gal-niche-filters" role="tablist" aria-label="Filter by niche">
          {NICHE_FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              role="tab"
              aria-selected={activeNiche === f.key}
              className={`gal-nf${activeNiche === f.key ? " on" : ""}`}
              onClick={() => handleNicheChange(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>
      <p className="gal-search-caption">tell us what you're looking for, however you'd say it</p>

      <div className="gal-grid-page">
        {/* 2.5. PAID SPOT (new) — one slot only, additive not exclusive.
            Same card component as everywhere else; the only difference
            is the gray "Paid Spot" tag. Sits before Fresh Moments so
            it's guaranteed visible to anyone who scrolls at all,
            without sharing space with the editorial hero above it. */}
        {paidItem && (
          <div className="gal-paid-section">
            <div className="gal-grid-head">
              <span className="gal-gh-title">Paid <em>Spot</em></span>
              <span className="gal-gh-hand">a creator featuring their own content</span>
              <div className="gal-gh-line" />
            </div>
            <div className="gal-paid-single">
              {paidItem.type === "drop" ? (
                <DropCard
                  drop={paidItem.drop}
                  creator={paidItem.creator}
                  access={getDropAccess(paidItem.drop, purchasedDropIds, subscribedCreatorIds)}
                  isPaid
                />
              ) : (
                <CreatorCard
                  creator={paidItem.creator}
                  openDropCount={paidItem.creator.openCount}
                  followingIds={followingIds}
                  isPaid
                />
              )}
            </div>
          </div>
        )}

        {/* 3. FRESH MOMENTS — chronological, horizontal rail. */}
        {freshMomentsRail.length > 0 && (
          <>
            <div className="gal-grid-head">
              <IconClock />
              <span className="gal-gh-title">
                Fresh <em>Moments</em>
              </span>
              <span className="gal-gh-hand">just posted</span>
              <div className="gal-gh-line" />
              <Link to="/gallery/fresh" className="gal-gh-seeall">
                See all {freshMomentsTotal} →
              </Link>
            </div>
            <div className="gal-rail">
              {freshMomentsRail.map((drop, i) => {
                const creator =
                  creatorById.get(drop.creatorId) ||
                  normalizeCreator(drop.creatorId, { displayName: "Creator", avatarUrl: "" });
                return (
                  <div className="gal-rail-item" key={drop.id}>
                    <DropCard
                      drop={drop}
                      creator={creator}
                      access={getDropAccess(drop, purchasedDropIds, subscribedCreatorIds)}
                      compact
                      index={i}
                    />
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* 4. CLOSING NOW / Almost Locked — unchanged structure, boost
            sort removed. Ranked purely by honest views-remaining. */}
        <div className="gal-grid-head">
          <span className="gal-gh-title">
            Closing <em>now</em>
          </span>
          <span className="gal-gh-hand">act before the window closes</span>
          <div className="gal-gh-line" />
          <div className="gal-gh-live">
            <span className="gal-ghl-dot" />
            {openDropCount} drops open
          </div>
        </div>

        <div className="gal-closing-grid">
          {closingDrops.length === 0 ? (
            <div className="gal-empty">
              <p>Nothing open in this niche right now — check back soon.</p>
            </div>
          ) : (
            closingDrops.map((drop, i) => {
              const creator =
                creatorById.get(drop.creatorId) ||
                normalizeCreator(drop.creatorId, { displayName: "Creator", avatarUrl: "" });
              return (
                <DropCard
                  key={drop.id}
                  drop={drop}
                  creator={creator}
                  access={getDropAccess(drop, purchasedDropIds, subscribedCreatorIds)}
                  index={i}
                />
              );
            })
          )}
        </div>

        {/* 5. RISING · NEW TO 100VO — reserved slots, never earned
            by performance, order is a fair session-seeded shuffle. */}
        {risingRail.length > 0 && (
          <>
            <div className="gal-grid-sec">
              <IconSpark />
              <span className="gal-gs-title">
                Rising · new to <em>100VO</em>
              </span>
              <span className="gal-gs-hand">reserved spots — never earned by performance</span>
              <div className="gal-gs-line" />
              <Link to="/gallery/rising" className="gal-gh-seeall">
                See all {risingTotal} →
              </Link>
            </div>
            <div className="gal-rail">
              {risingRail.map((creator, i) => (
                <div className="gal-rail-item" key={creator.id}>
                  <CreatorCard
                    creator={creator}
                    openDropCount={creator.openCount}
                    followingIds={followingIds}
                    isNew
                    compact
                    index={i}
                  />
                </div>
              ))}
            </div>
          </>
        )}

        {/* 6. CREATORS TO FOLLOW — unchanged structure, follower-count
            tiebreak removed in favor of the session-seeded shuffle. */}
        {creatorsToFollow.length > 0 && (
          <>
            <div className="gal-grid-sec">
              <span className="gal-gs-title">
                Creators to <em>follow</em>
              </span>
              <span className="gal-gs-hand">you don&apos;t follow any of these yet</span>
              <div className="gal-gs-line" />
            </div>

            <div className="gal-disc-grid">
              {creatorsToFollow.map((creator, i) => (
                <CreatorCard
                  key={creator.id}
                  creator={creator}
                  openDropCount={creator.openCount}
                  followingIds={followingIds}
                  index={i}
                />
              ))}
            </div>
          </>
        )}

        {/* 7. NEAR YOU — new. Opt-in by existing profile city field. */}
        {nearYouCreators.length > 0 && (
          <>
            <div className="gal-grid-sec">
              <IconPin />
              <span className="gal-gs-title">
                Near <em>you</em>
                {currentUserCity ? ` — ${currentUserCity}` : ""}
              </span>
              <span className="gal-gs-hand">creators posting close to home</span>
              <div className="gal-gs-line" />
            </div>
            <div className="gal-rail">
              {nearYouCreators.map((creator, i) => (
                <div className="gal-rail-item" key={creator.id}>
                  <CreatorCard
                    creator={creator}
                    openDropCount={creator.openCount}
                    followingIds={followingIds}
                    compact
                    index={i}
                  />
                </div>
              ))}
            </div>
          </>
        )}

        {/* 8. NUDGE CARDS — unchanged */}
        {streakCount > 0 && (
          <SessionDismissible id={NUDGE_IDS.GALLERY_STREAK}>
            <div className="gal-streak-card">
              <div className="gal-sk-icon">
                <IconBolt />
              </div>
              <div className="gal-sk-body">
                <div className="gal-sk-title">
                  {streakCount} new creator{streakCount === 1 ? "" : "s"} discovered this week
                </div>
                <p className="gal-sk-sub">
                  You&apos;re on a streak. Find one more today and earn 15 bonus credits — they land in
                  your account immediately.
                </p>
                <span className="gal-sk-hand">keep going — you&apos;re on a roll</span>
              </div>
              <Link to="/perks" className="gal-sk-cta">
                Earn +15 credits
              </Link>
            </div>
          </SessionDismissible>
        )}

        {reengagementCreator?.drop && (
          <SessionDismissible id={NUDGE_IDS.GALLERY_REENGAGE}>
            <div className="gal-nudge-card">
              <ProfileAvatarLink
                user={reengagementCreator.creator}
                src={reengagementCreator.creator.avatarUrl || reengagementCreator.creator.avatar}
                alt={reengagementCreator.creator.displayName}
                className="gal-nudge-av-link"
                imgClassName="gal-nudge-av"
              />
              <div className="gal-nudge-body">
                <p className="gal-nudge-text">
                  A creator you might like —{" "}
                  <ProfileNameLink user={reengagementCreator.creator}>
                    {reengagementCreator.creator.displayName}
                  </ProfileNameLink>
                  {" "}still has a drop open. {reengagementCreator.drop.viewCount || 0} people in so far.
                </p>
                <span className="gal-nudge-hand">
                  {reengagementCreator.moment?.caption
                    ? reengagementCreator.moment.caption
                    : `only ${viewsLeft(reengagementCreator.drop)} spots left before it closes`}
                </span>
              </div>
              <Link to={`/drop/${reengagementCreator.drop.id}`} className="gal-nudge-btn">
                View drop →
              </Link>
            </div>
          </SessionDismissible>
        )}

        {/* 9. DARK CTA — unchanged */}
        <SessionDismissible id={NUDGE_IDS.GALLERY_END} tone="dark">
        <div className="gal-end-card">
          <div className="gal-end-title">
            You&apos;ve seen <em>{creatorsSeenCount} creator{creatorsSeenCount === 1 ? "" : "s"}</em>{" "}
            today.
          </div>
          <span className="gal-end-hand">
            {Math.max(creators.length - creatorsSeenCount, 0)} more are posting right now
          </span>
          <p className="gal-end-stat">
            Every creator you follow gives you <strong>early access to their drops</strong>
            <br />
            before anyone else sees them. That&apos;s the whole point of following.
          </p>
          <button
            type="button"
            className="gal-end-btn"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          >
            Keep Discovering →
          </button>
        </div>
        </SessionDismissible>
      </div>
    </div>
  );
}